import { __assign } from "tslib";
import * as React from 'react';
import memoizeOne from 'memoize-one';
import { argsAreEqual, getTreatment } from './utils';
import { useSplitClient } from './useSplitClient';
function evaluateFeatureFlagWithConfig(client, _lastUpdate, names, attributes, _clientAttributes, _flagSets, options, factory) {
    return client && client.getStatus().isOperational ?
        client.getTreatmentWithConfig(names[0], attributes, options) :
        getTreatment(names[0], true, factory);
}
function memoizeGetTreatmentWithConfig() {
    return memoizeOne(evaluateFeatureFlagWithConfig, argsAreEqual);
}
/**
 * `useTreatmentWithConfig` is a hook that returns an Split Context object extended with a `treatment` property.
 * It uses the `useSplitClient` hook to access the client, and invokes the `client.getTreatmentWithConfig()` method.
 *
 * @param options - An options object with a feature flag name to evaluate, and an optional `attributes` and `splitKey` values to configure the client.
 * @returns A Split Context object extended with a TreatmentWithConfig instance, that might be a control treatment if the client is not available or ready, or if the provided feature flag name does not exist.
 *
 * @example
 * ```js
 * const { treatment: { treatment, config }, isReady, isReadyFromCache, hasTimedout, lastUpdate, ... } = useTreatmentWithConfig({ name: 'feature_1'});
 * ```
 *
 * @see {@link https://developer.harness.io/docs/feature-management-experimentation/sdks-and-infrastructure/client-side-sdks/javascript-sdk/#get-treatments-with-configurations}
 */
export function useTreatmentWithConfig(options) {
    var context = useSplitClient(__assign(__assign({}, options), { attributes: undefined }));
    var factory = context.factory, client = context.client, lastUpdate = context.lastUpdate;
    var name = options.name, attributes = options.attributes, properties = options.properties;
    var getTreatmentWithConfig = React.useMemo(memoizeGetTreatmentWithConfig, []);
    // Shallow copy `client.getAttributes` result for memoization, as it returns the same reference unless `client.clearAttributes` is invoked.
    // Note: the same issue occurs with the `names` and `attributes` arguments if they are mutated directly by the user instead of providing a new object.
    var treatment = getTreatmentWithConfig(client, lastUpdate, [name], attributes, client ? __assign({}, client.getAttributes()) : {}, undefined, properties && { properties: properties }, factory);
    return __assign(__assign({}, context), { treatment: treatment });
}
